// File: src/theme/index.js
// Description: Comprehensive Material-UI theme with modern design system, professional colors, and enhanced component variants.

import { createTheme } from '@mui/material/styles';

// Custom color palette with semantic meaning
const colors = {
  // Primary palette - Modern blue gradient
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Secondary palette - Complementary purple/indigo
  secondary: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  // Neutral palette - Sophisticated grays
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  // Semantic colors
  success: {
    main: '#10b981',
    light: '#34d399',
    dark: '#059669',
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#dc2626',
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
  },
};

// Enhanced typography scale
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.25rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '1.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.025em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
};

// Enhanced component variants
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        padding: '12px 24px',
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        minHeight: '44px', // Touch-friendly minimum height
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
        '&:active': {
          transform: 'translateY(0px)',
          transition: 'transform 0.1s ease-in-out',
        },
      },
      contained: {
        background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
        '&:hover': {
          background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`,
        },
      },
      outlined: {
        borderWidth: '2px',
        '&:hover': {
          borderWidth: '2px',
          backgroundColor: colors.primary[50],
        },
      },
      // Mobile-optimized button sizes
      sizeSmall: {
        padding: '8px 16px',
        minHeight: '36px',
        fontSize: '0.875rem',
      },
      sizeLarge: {
        padding: '16px 32px',
        minHeight: '52px',
        fontSize: '1.1rem',
      },
    },
    variants: [
      {
        props: { variant: 'gradient' },
        style: {
          background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.secondary[500]} 100%)`,
          color: 'white',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.secondary[600]} 100%)`,
          },
        },
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          minHeight: '48px', // Touch-friendly input height
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary[300],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary[500],
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.95rem',
        },
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        border: `3px solid ${colors.neutral[100]}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '20px',
        fontWeight: 500,
        minHeight: '32px', // Touch-friendly chip height
        '& .MuiChip-label': {
          padding: '0 12px',
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  // Mobile-optimized drawer
  MuiDrawer: {
    styleOverrides: {
      paper: {
        border: 'none',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        '& .MuiListItem-root': {
          borderRadius: '8px',
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: colors.primary[50],
          },
        },
      },
    },
  },
  // Mobile-optimized stepper
  MuiStepper: {
    styleOverrides: {
      root: {
        '& .MuiStepLabel-root': {
          '& .MuiStepLabel-label': {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        },
      },
    },
  },
  // Mobile-optimized list
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        margin: '2px 0',
        '&:hover': {
          backgroundColor: colors.primary[50],
        },
      },
    },
  },
  // Mobile-optimized icons
  MuiIconButton: {
    styleOverrides: {
      root: {
        minWidth: '44px', // Touch-friendly minimum width
        minHeight: '44px', // Touch-friendly minimum height
        '&:hover': {
          transform: 'scale(1.05)',
          transition: 'transform 0.2s ease-in-out',
        },
      },
    },
  },
  // Mobile-optimized alerts
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        '& .MuiAlert-message': {
          fontSize: '0.9rem',
        },
      },
    },
  },
  // Mobile-optimized tooltips
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '0.8rem',
        padding: '8px 12px',
        borderRadius: '8px',
      },
    },
  },
};

// Enhanced breakpoints for better mobile experience
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// Enhanced spacing system (8px grid with mobile considerations)
const spacing = 8;

// Mobile-optimized shadows
const shadows = [
  'none',
  '0 1px 2px rgba(0, 0, 0, 0.05)',
  '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
  '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.05)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 20px 25px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 25px 50px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 50px 100px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 100px 200px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 200px 400px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 400px 819200px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 800px 1600px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 1600px 3200px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 3200px 6400px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 6400px 12800px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 12800px 25600px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 25600px 51200px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 51200px 102400px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 102400px 204800px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 204800px 409600px rgba(0, 0, 0, 0.1)',
  '0 0 0 1px rgba(0, 0, 0, 0.05), 0 409600px 819200px rgba(0, 0, 0, 0.1)',
];

const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary[600],
      light: colors.primary[400],
      dark: colors.primary[800],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[600],
      light: colors.secondary[400],
      dark: colors.secondary[800],
      contrastText: '#ffffff',
    },
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: {
      default: colors.neutral[50],
      paper: '#ffffff',
    },
    text: {
      primary: colors.neutral[900],
      secondary: colors.neutral[600],
    },
    divider: colors.neutral[200],
  },
  typography: {
    fontFamily: typography.fontFamily,
    // Mobile-optimized typography
    h1: {
      ...typography.h1,
      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
    },
    h2: {
      ...typography.h2,
      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
    },
    h3: {
      ...typography.h3,
      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '1.875rem' },
    },
    h4: {
      ...typography.h4,
      fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
    },
    h5: {
      ...typography.h5,
      fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.25rem' },
    },
    h6: {
      ...typography.h6,
      fontSize: { xs: '1rem', sm: '1.125rem', md: '1.125rem' },
    },
    body1: {
      ...typography.body1,
      fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' },
    },
    body2: {
      ...typography.body2,
      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
    },
    button: {
      ...typography.button,
      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
    },
  },
  spacing,
  components,
  breakpoints,
  shape: {
    borderRadius: 12,
  },
  shadows,
});

export default theme;
